import { cloneDeep } from 'lodash';
import { useIntl } from 'react-intl';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import {
  TurnTrackingHistoryType,
  TurnTrackingPartyType,
} from '~/types/TurnTracking';

import { calculateDuration, partyNameFromPartyIdAndParties } from './utils';

interface Props {
  parties: TurnTrackingPartyType[];
  historyEntry: TurnTrackingHistoryType;
  isTicketFinished: boolean;
  onSetEditingEntry?: (entry: TurnTrackingHistoryType) => void;
  onShowEditPanel?: () => void;
}

export const TurnTrackingHistory = ({
  parties,
  historyEntry,
  isTicketFinished,
  onSetEditingEntry,
  onShowEditPanel,
}: Props) => {
  const intl = useIntl();
  const { startTime, endTime, partyId } = historyEntry;

  return (
    <Layout justify="space-between" align="center">
      <Layout direction="column">
        <Text color="text.secondary">
          <Text variant="body-bold" color="text.secondary">
            {partyNameFromPartyIdAndParties(partyId, parties)}{' '}
          </Text>
          {calculateDuration(startTime, endTime || new Date())}{' '}
          {intl.formatMessage({
            id: 'TurnTrackingHistory.daysLabel',
            defaultMessage: 'Days',
            description: 'Label for duration in days',
          })}
        </Text>
        <Text color="text.secondary">
          <DateText date={startTime} format="short_datetime" variant="tiny" /> -{' '}
          {endTime ? (
            <DateText date={endTime} format="short_datetime" variant="tiny" />
          ) : (
            <Text variant="tiny" color="text.secondary">
              {intl.formatMessage({
                id: 'TurnTrackingHistory.presentLabel',
                defaultMessage: 'Present',
                description: 'Label indicating current/ongoing time period',
              })}
            </Text>
          )}
        </Text>
      </Layout>
      {onSetEditingEntry && onShowEditPanel && !isTicketFinished && (
        <ActionsMenu
          actions={[
            {
              label: intl.formatMessage({
                id: 'TurnTrackingHistory.editDateTimeAction',
                defaultMessage: 'Edit date and time',
                description: 'Action menu item for editing date and time',
              }),
              value: 'Edit date and time',
            },
          ]}
          name="more actions"
          onActionClick={() => {
            onShowEditPanel();
            onSetEditingEntry(cloneDeep(historyEntry));
          }}
        />
      )}
    </Layout>
  );
};
